import { z } from "zod";

import { jurisdictionProfileSchema } from "./cableContract";
import { calculationRunSummarySchema } from "./calculationRun";
import {
  areaOriginSchema,
  conductorMaterialSchema,
  conductorRoleSchema,
  disconnectionTimeOriginSchema,
  disconnectionTimeSourceSchema,
  earthFaultLoopStatusSchema,
  earthingStatusSchema,
  earthingWarningCodeSchema,
  ELECTRODE_DIMENSIONS,
  electrodeKindSchema,
  energyBasisSchema,
  faultCurrentOriginSchema,
  faultTypeSchema,
  K_TABLE_COLUMNS,
  kOriginSchema,
  kTableIdSchema,
  seasonalFactorOriginSchema,
  shortCircuitCaseSchema,
  systemKindSchema,
  targetOriginSchema,
  ALWAYS_STUDY_INFORMATION_CODES,
} from "./earthingContract";
import { exactDecimalSchema } from "./faultContract";
import {
  CHOOSE_A_FAULT_RUN,
  type FaultRunChoice,
  formatApiError,
  listFaultRunsForRevision,
  requestSignal,
} from "./faultLink";
import { ApiError } from "./http";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const optionalSource = (maxLength = 300) => z.string().trim().min(1).max(maxLength).optional();

const RUNS_URL = "/api/v1/electrical/earthing/runs";
const EARTH_FAULT_LOOP_RUNS_URL = "/api/v1/electrical/earth-fault-loop/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** D3: a bus earth-fault current is never applied to a circuit protective conductor. */
export const CIRCUIT_ROLE_FAULT_LINK_REFUSED =
  "A bus earth-fault current is not applied to a circuit protective conductor; link the " +
  "circuit's EOS-05d row or declare the current with its source.";

/** The selector's own sentence for a row that names no earth-fault loop run of the revision. */
export const CHOOSE_A_DISCONNECTION_ROW =
  "Choose an earth-fault loop run of this project revision and one of its circuit rows.";

// The fault-run selector (list and sentence) is the one the protection studies share; an
// earthing study's board conductor names a stored EOS-04 earth-fault run in the same way.
export { CHOOSE_A_FAULT_RUN, listFaultRunsForRevision };
export type { FaultRunChoice };

// The choices a row and an electrode need; the backend imposes no default on them.
export const REQUIRED_ROW_CHOICES = ["role"] as const;
export const REQUIRED_ELECTRODE_CHOICES = ["kind"] as const;
export const REQUIRED_STUDY_CHOICES = ["system_kind"] as const;

/** One declared figure with its source; the unit is fixed by the field that holds it. */
export const declaredRequestSchema = z
  .object({ value: exactDecimalSchema, source: optionalSource() })
  .strict();

/** The held k table and its column the engineer declares (D6). */
export const kTableCaseRequestSchema = z
  .object({ table: kTableIdSchema, column: z.string().trim().min(1).max(40) })
  .strict()
  .superRefine((value, ctx) => {
    const columns: readonly string[] = K_TABLE_COLUMNS[value.table];
    if (!columns.includes(value.column)) {
      ctx.addIssue({
        code: "custom",
        message: `the column must be one of ${columns.join(", ")} for ${value.table}`,
        path: ["column"],
      });
    }
  });

// One protective conductor: its role, its links and its declarations (section 5).
export const conductorRowRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    role: conductorRoleSchema.optional(),
    fault_run_id: z.string().optional(),
    disconnection_run_id: z.string().optional(),
    disconnection_row_code: z.string().trim().min(1).max(50).optional(),
    declared_fault_current_a: declaredRequestSchema.optional(),
    declared_let_through_energy_a2s: declaredRequestSchema.optional(),
    declared_disconnection_time_s: declaredRequestSchema.optional(),
    declared_area_mm2: declaredRequestSchema.optional(),
    conductor_material: conductorMaterialSchema.optional(),
    declared_k: declaredRequestSchema.optional(),
    k_table_case: kTableCaseRequestSchema.optional(),
    sld_element_code: z.string().trim().min(1).max(50).optional(),
    notes: optionalRequestText(500),
  })
  .strict()
  .superRefine((row, ctx) => {
    for (const field of REQUIRED_ROW_CHOICES) {
      if (row[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
    if (row.role === "CIRCUIT_PROTECTIVE" && row.fault_run_id !== undefined) {
      ctx.addIssue({ code: "custom", message: CIRCUIT_ROLE_FAULT_LINK_REFUSED, path: ["fault_run_id"] });
    } else if (row.fault_run_id !== undefined && !UUID.test(row.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }
    if ((row.disconnection_run_id === undefined) !== (row.disconnection_row_code === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "name the earth-fault loop run and the circuit row code together, or neither",
        path: ["disconnection_row_code"],
      });
    } else if (row.disconnection_run_id !== undefined && !UUID.test(row.disconnection_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_DISCONNECTION_ROW, path: ["disconnection_run_id"] });
    }
  });

// One earth electrode: its kind with that kind's dimensions, the count and the factor.
export const electrodeRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    kind: electrodeKindSchema.optional(),
    rod_length_m: exactDecimalSchema.optional(),
    rod_diameter_mm: exactDecimalSchema.optional(),
    plate_length_m: exactDecimalSchema.optional(),
    plate_width_m: exactDecimalSchema.optional(),
    strip_length_m: exactDecimalSchema.optional(),
    strip_width_mm: exactDecimalSchema.optional(),
    conductor_length_m: exactDecimalSchema.optional(),
    conductor_diameter_mm: exactDecimalSchema.optional(),
    dimension_source: optionalSource(),
    material: z.string().trim().min(1).max(100).optional(),
    count_in_parallel: z.number().int().min(1).max(100).optional(),
    parallel_factor: declaredRequestSchema.optional(),
    spacing_m: exactDecimalSchema.optional(),
    notes: optionalRequestText(500),
  })
  .strict()
  .superRefine((electrode, ctx) => {
    if (electrode.kind === undefined) {
      ctx.addIssue({
        code: "invalid_type",
        expected: "string",
        received: "undefined",
        message: "kind is required",
        path: ["kind"],
      });
      return;
    }
    const needed: readonly string[] = ELECTRODE_DIMENSIONS[electrode.kind];
    for (const names of Object.values(ELECTRODE_DIMENSIONS)) {
      for (const name of names) {
        const value = electrode[name as keyof typeof electrode];
        if (needed.includes(name) && value === undefined) {
          ctx.addIssue({ code: "custom", message: `declare ${name} for a ${electrode.kind} electrode`, path: [name] });
        }
        if (!needed.includes(name) && value !== undefined) {
          ctx.addIssue({ code: "custom", message: `${name} does not belong to a ${electrode.kind} electrode`, path: [name] });
        }
      }
    }
    if (electrode.parallel_factor !== undefined && (electrode.count_in_parallel ?? 1) === 1) {
      ctx.addIssue({
        code: "custom",
        message: "a utilisation factor is declared only for more than one electrode in parallel",
        path: ["parallel_factor"],
      });
    }
  });

export const soilRequestSchema = z
  .object({
    resistivity_ohm_m: exactDecimalSchema,
    test_method: z.string().trim().min(1).max(200).optional(),
    test_date: z.string().trim().min(1).max(40).optional(),
    source: optionalSource(),
    seasonal_factor: declaredRequestSchema.optional(),
  })
  .strict();

// One arrangement at one bus with 0..n conductor rows and 0..n electrodes, at least one of
// either; the soil and the target declared or left out (NOT ESTABLISHED, never assumed).
export const earthingRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    bus_code: z.string().trim().min(1).max(50),
    system_kind: systemKindSchema.optional(),
    system_voltage_v: declaredRequestSchema,
    sld_bus_code: z.string().trim().min(1).max(50).optional(),
    conductors: z.array(conductorRowRequestSchema).max(200).optional(),
    electrodes: z.array(electrodeRequestSchema).max(200).optional(),
    soil: soilRequestSchema.optional(),
    target_resistance_ohm: declaredRequestSchema.optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    for (const field of REQUIRED_STUDY_CHOICES) {
      if (values[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
    const conductors = values.conductors ?? [];
    const electrodes = values.electrodes ?? [];
    if (conductors.length === 0 && electrodes.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "a study carries at least one conductor row or one electrode",
        path: ["conductors"],
      });
    }
    for (const [name, items] of [
      ["conductors", conductors],
      ["electrodes", electrodes],
    ] as const) {
      const codes = items.map((item) => item.code);
      if (new Set(codes).size !== codes.length) {
        ctx.addIssue({ code: "custom", message: `${name} codes must be unique within a study`, path: [name] });
      }
    }
  });

export const earthingWarningSchema = z
  .object({ code: earthingWarningCodeSchema, message: z.string() })
  .strict();

export const declaredResponseSchema = z
  .object({ value: z.string(), source: z.string().nullable() })
  .strict();

export const linkedEarthFaultRunResponseSchema = z
  .object({
    fault_run_id: z.string().uuid(),
    study_code: z.string(),
    revision_number: z.number().int(),
    fault_bus_code: z.string(),
    calculation_case: shortCircuitCaseSchema,
    fault_type: faultTypeSchema,
    earth_fault_current_ka: z.string(),
    voltage_factor: z.string().nullable(),
  })
  .strict();

export const linkedDisconnectionRowResponseSchema = z
  .object({
    run_id: z.string().uuid(),
    study_code: z.string(),
    revision_number: z.number().int(),
    row_code: z.string(),
    bus_code: z.string(),
    run_status: earthFaultLoopStatusSchema,
    row_status: earthFaultLoopStatusSchema,
    disconnection_time_origin: disconnectionTimeOriginSchema,
    required_disconnection_time_s: z.string().nullable(),
    fault_current_a: z.string().nullable(),
    protective_conductor_area_mm2: z.string().nullable(),
    conductor_material: conductorMaterialSchema.nullable(),
    disconnection_assured: z.boolean().nullable(),
    closes_thermal_not_checked: z.boolean(),
  })
  .strict();

// One conductor row as checked: every figure on its stated basis, formed exactly when its
// inputs exist (null otherwise; F1: a field added later is optional on this side).
export const conductorRowResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    role: conductorRoleSchema,
    linked_fault_run: linkedEarthFaultRunResponseSchema.nullable(),
    disconnection_row: linkedDisconnectionRowResponseSchema.nullable(),
    fault_current_origin: faultCurrentOriginSchema,
    fault_current_a: z.string().nullable(),
    fault_current_source: z.string().nullable(),
    disconnection_time_source: disconnectionTimeSourceSchema,
    disconnection_time_s: z.string().nullable(),
    disconnection_time_source_text: z.string().nullable(),
    energy_basis: energyBasisSchema,
    let_through_energy_a2s: z.string().nullable(),
    let_through_energy_source: z.string().nullable(),
    k_origin: kOriginSchema,
    k_factor: z.string().nullable(),
    k_source: z.string().nullable(),
    k_locator: z.string().nullable(),
    k_boundary_conditions: z.string().nullable(),
    conductor_material: conductorMaterialSchema.nullable(),
    area_origin: areaOriginSchema,
    declared_area_mm2: z.string().nullable(),
    declared_area_source: z.string().nullable(),
    required_area_mm2: z.string().nullable(),
    margin_mm2: z.string().nullable(),
    ratio: z.string().nullable(),
    met: z.boolean().nullable(),
    sld_element_code: z.string().nullable(),
    status: earthingStatusSchema,
    warnings: z.array(earthingWarningSchema),
    notes: z.string().nullable(),
  })
  .strict();

export const electrodeResponseSchema = z
  .object({
    code: z.string(),
    kind: electrodeKindSchema,
    dimensions: z.record(z.string(), z.string()),
    dimension_source: z.string().nullable(),
    material: z.string().nullable(),
    count_in_parallel: z.number().int(),
    parallel_factor: declaredResponseSchema.nullable(),
    spacing_m: z.string().nullable(),
    design_resistivity_ohm_m: z.string().nullable(),
    single_resistance_ohm: z.string().nullable(),
    formula_locator: z.string().nullable(),
    group_resistance_ohm: z.string().nullable(),
    status: earthingStatusSchema,
    warnings: z.array(earthingWarningSchema),
    notes: z.string().nullable(),
  })
  .strict();

export const soilResponseSchema = z
  .object({
    resistivity_ohm_m: z.string(),
    test_method: z.string().nullable(),
    test_date: z.string().nullable(),
    source: z.string().nullable(),
    seasonal_factor: declaredResponseSchema.nullable(),
  })
  .strict();

export const earthingResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    bus_code: z.string(),
    system_kind: systemKindSchema,
    system_voltage_v: declaredResponseSchema,
    sld_bus_code: z.string().nullable(),
    soil: soilResponseSchema.nullable(),
    seasonal_factor_origin: seasonalFactorOriginSchema,
    design_resistivity_ohm_m: z.string().nullable(),
    target_origin: targetOriginSchema,
    target_resistance_ohm: declaredResponseSchema.nullable(),
    arrangement_resistance_ohm: z.string().nullable(),
    resistance_margin_ohm: z.string().nullable(),
    resistance_ratio: z.string().nullable(),
    resistance_met: z.boolean().nullable(),
    jurisdiction_profile: jurisdictionProfileSchema,
    conductors: z.array(conductorRowResponseSchema),
    electrodes: z.array(electrodeResponseSchema),
    status: earthingStatusSchema,
    warnings: z.array(earthingWarningSchema),
    notes: z.string().nullable(),
  })
  .strict();

export const earthingRunCreateRequestSchema = z
  .object({
    study: earthingRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const earthingRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: earthingResponseSchema,
  })
  .strict();

// What the disconnection-row selector shows from the earth-fault loop runs list and from one
// run's frozen result: the run summaries, and the circuit row codes of the chosen run with the
// figures the earthing row will read (display only; the backend reads them again when the
// study is calculated).
export const earthFaultLoopRunChoiceSchema = z
  .object({
    id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int(),
    design_check_status: z.string(),
    jurisdiction_profile: z.string(),
  })
  .strict();

const earthFaultLoopRunListSchema = z
  .object({ items: z.array(calculationRunSummarySchema) })
  .strict();

export const disconnectionRowChoiceSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    required_disconnection_time_s: z.string().nullable(),
    fault_current_a: z.string().nullable(),
    protective_conductor_area_mm2: z.string().nullable(),
    conductor_material: z.string().nullable(),
    disconnection_assured: z.boolean().nullable(),
  })
  .strict();

const earthFaultLoopRunDetailSchema = calculationRunSummarySchema
  .extend({
    input_snapshot: z.record(z.string(), z.unknown()),
    result_snapshot: z.object({ rows: z.array(z.record(z.string(), z.unknown())) }).passthrough(),
    warnings_snapshot: z.array(z.unknown()),
    references_snapshot: z.record(z.string(), z.unknown()),
  })
  .strict();

export type DeclaredRequest = z.infer<typeof declaredRequestSchema>;
export type KTableCaseRequest = z.infer<typeof kTableCaseRequestSchema>;
export type ConductorRowRequest = z.infer<typeof conductorRowRequestSchema>;
export type ElectrodeRequest = z.infer<typeof electrodeRequestSchema>;
export type SoilRequest = z.infer<typeof soilRequestSchema>;
export type EarthingRequest = z.infer<typeof earthingRequestSchema>;
export type EarthingWarning = z.infer<typeof earthingWarningSchema>;
export type DeclaredResponse = z.infer<typeof declaredResponseSchema>;
export type LinkedEarthFaultRunResponse = z.infer<typeof linkedEarthFaultRunResponseSchema>;
export type LinkedDisconnectionRowResponse = z.infer<typeof linkedDisconnectionRowResponseSchema>;
export type ConductorRowResponse = z.infer<typeof conductorRowResponseSchema>;
export type ElectrodeResponse = z.infer<typeof electrodeResponseSchema>;
export type SoilResponse = z.infer<typeof soilResponseSchema>;
export type EarthingResponse = z.infer<typeof earthingResponseSchema>;
export type EarthingRunCreateRequest = z.infer<typeof earthingRunCreateRequestSchema>;
export type EarthingRunResponse = z.infer<typeof earthingRunResponseSchema>;
export type EarthFaultLoopRunChoice = z.infer<typeof earthFaultLoopRunChoiceSchema>;
export type DisconnectionRowChoice = z.infer<typeof disconnectionRowChoiceSchema>;

export { ALWAYS_STUDY_INFORMATION_CODES };

/**
 * Calculate an earthing study and persist it as a run revision.
 *
 * With a project revision id the run is stored under that revision of the selected project -
 * and every linked run must then belong to the same revision; without one it belongs to no
 * project. An unchanged study returns the stored revision with HTTP 200 instead of a new one
 * (A11); both are valid here.
 */
export async function createEarthingRun(
  payload: EarthingRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<EarthingRunResponse> {
  const validatedPayload = earthingRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Earthing"), response.status);
  }

  const parsed = earthingRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS earthing runs API.");
  }

  return parsed.data;
}

/**
 * The earth-fault loop runs stored under one project revision, newest first, as the
 * disconnection-row selector lists them. The list carries run summaries only; the rows of
 * the chosen run come from listDisconnectionRows.
 */
export async function listEarthFaultLoopRunsForRevision(
  projectRevisionId: string,
  signal?: AbortSignal,
): Promise<EarthFaultLoopRunChoice[]> {
  const query = new URLSearchParams({ project_revision_id: projectRevisionId });

  const response = await fetch(`${EARTH_FAULT_LOOP_RUNS_URL}?${query.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Earth-fault loop run list"), response.status);
  }

  const parsed = earthFaultLoopRunListSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS earth-fault loop runs API.");
  }

  return parsed.data.items.map((item) =>
    earthFaultLoopRunChoiceSchema.parse({
      id: item.id,
      calculation_key: item.calculation_key,
      revision_number: item.revision_number,
      design_check_status: item.design_check_status,
      jurisdiction_profile: item.jurisdiction_profile,
    }),
  );
}

function textOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : value === null || value === undefined ? null : String(value);
}

/**
 * The circuit rows one stored earth-fault loop run holds, for the selector beside a conductor
 * row: the code, the name and the figures the earthing row will read (time, current, area,
 * material, assured). Display only: the backend reads the frozen row again when the study is
 * calculated.
 */
export async function listDisconnectionRows(
  runId: string,
  signal?: AbortSignal,
): Promise<DisconnectionRowChoice[]> {
  const response = await fetch(`${EARTH_FAULT_LOOP_RUNS_URL}/${runId}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Earth-fault loop run"), response.status);
  }

  const parsed = earthFaultLoopRunDetailSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS earth-fault loop runs API.");
  }

  return parsed.data.result_snapshot.rows.map((row) =>
    disconnectionRowChoiceSchema.parse({
      code: String(row.code),
      name: typeof row.name === "string" ? row.name : String(row.code),
      required_disconnection_time_s: textOrNull(row.required_disconnection_time_s),
      fault_current_a: textOrNull(row.fault_current_a),
      protective_conductor_area_mm2: textOrNull(row.protective_conductor_area_mm2),
      conductor_material: textOrNull(row.conductor_material),
      disconnection_assured: typeof row.disconnection_assured === "boolean" ? row.disconnection_assured : null,
    }),
  );
}
